import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../components/Layout/AdminLayout';
import { PageHeader } from '../components/common';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Snackbar,
  InputAdornment,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Add, Edit as EditIcon, Visibility, Lock, TrendingUp, Search, MoreVert } from '@mui/icons-material';
import { useFinancialPeriods, usePermission } from '../hooks';
import { revenueService } from '../services';
import { colors } from '../theme/theme';

export const FinancialPeriodsPage = () => {
  const { can } = usePermission();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<any>(null);
  const [formData, setFormData] = useState({ name: '', start_date: '', end_date: '', period_type: 'financial_year' });
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [closureDialog, setClosureDialog] = useState<{ open: boolean; periodId: number | null; summary: any }>({ open: false, periodId: null, summary: null });
  const [closureLoading, setClosureLoading] = useState(false);
  const [setupPrompt, setSetupPrompt] = useState<{ open: boolean; periodId: number | null; periodName: string }>({ open: false, periodId: null, periodName: '' });
  const [actionMenu, setActionMenu] = useState<{ anchorEl: HTMLElement | null; row: any }>({ anchorEl: null, row: null });

  const [isEditing, setIsEditing] = useState(false);

  const { periods, isLoading, error, createPeriod, updatePeriod, deactivatePeriod } = useFinancialPeriods({
    page: page + 1,
    per_page: rowsPerPage,
    search,
  });

  const handleOpenDialog = (period?: any, edit = false) => {
    setSelectedPeriod(period || null);
    setIsEditing(edit);
    if (period) {
      setFormData({
        name: period.name || '',
        start_date: period.start_date?.split('T')[0] || '',
        end_date: period.end_date?.split('T')[0] || '',
        period_type: period.period_type || 'financial_year',
      });
    } else {
      setFormData({ name: '', start_date: '', end_date: '', period_type: 'financial_year' });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPeriod(null);
    setFormData({ name: '', start_date: '', end_date: '', period_type: 'financial_year' });
  };

  const handleCreatePeriod = async () => {
    if (!formData.name || !formData.start_date || !formData.end_date) return;
    try {
      setSaving(true);
      const response = await createPeriod(formData);
      const newPeriod = (response as any)?.data || response;
      handleCloseDialog();
      // Show setup prompt instead of just a snackbar
      setSetupPrompt({
        open: true,
        periodId: newPeriod?.id || null,
        periodName: formData.name,
      });
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || err.response?.data?.message || 'Failed to create period';
      setSnackbar({ open: true, message: typeof errorMsg === 'string' ? errorMsg : 'Failed to create period', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePeriod = async () => {
    if (!selectedPeriod || !formData.name || !formData.start_date || !formData.end_date) return;
    try {
      setSaving(true);
      await updatePeriod({ id: selectedPeriod.id, data: formData });
      setSnackbar({ open: true, message: 'Financial period updated successfully', severity: 'success' });
      handleCloseDialog();
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || err.response?.data?.message || 'Failed to update period';
      setSnackbar({ open: true, message: typeof errorMsg === 'string' ? errorMsg : 'Failed to update period', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleClosePeriod = async (periodId: number) => {
    try {
      setClosureLoading(true);
      const res = await revenueService.getClosureSummary(periodId);
      setClosureDialog({ open: true, periodId, summary: res.data });
    } catch (err: any) {
      setSnackbar({ open: true, message: 'Failed to load period summary', severity: 'error' });
    } finally {
      setClosureLoading(false);
    }
  };

  const confirmClosePeriod = async () => {
    if (!closureDialog.periodId) return;
    try {
      setSaving(true);
      await deactivatePeriod(closureDialog.periodId);
      setClosureDialog({ open: false, periodId: null, summary: null });
      setSnackbar({ open: true, message: 'Financial period closed successfully', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to close period', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'default' => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'success';
      case 'closed':
        return 'error';
      case 'draft':
        return 'warning';
      default:
        return 'default';
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Period Name',
      flex: 1,
      minWidth: 200,
      display: 'flex',
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {params.value}
          </Typography>
          {params.row.period_type && (
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
              {params.row.period_type.replace('_', ' ')}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'start_date',
      headerName: 'Start Date',
      width: 140,
      align: 'center',
      headerAlign: 'center',
      display: 'flex',
      valueGetter: (value: any) => new Date(value).toLocaleDateString(),
    },
    {
      field: 'end_date',
      headerName: 'End Date',
      width: 140,
      align: 'center',
      headerAlign: 'center',
      display: 'flex',
      valueGetter: (value: any) => new Date(value).toLocaleDateString(),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      align: 'center',
      headerAlign: 'center',
      display: 'flex',
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getStatusColor(params.value)}
          size="small"
          sx={{ textTransform: 'capitalize', fontWeight: 500 }}
        />
      ),
    },
    {
      field: 'revenue_streams_count',
      headerName: 'Streams',
      width: 80,
      align: 'center',
      headerAlign: 'center',
      display: 'flex',
      valueGetter: (value: any) => value || 0,
    },
    {
      field: 'total_target',
      headerName: 'Target',
      width: 140,
      align: 'right',
      headerAlign: 'right',
      display: 'flex',
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={500}>
          {params.value ? `KES ${parseFloat(params.value).toLocaleString()}` : '-'}
        </Typography>
      ),
    },
    {
      field: 'progress',
      headerName: 'Progress',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      display: 'flex',
      renderCell: (params) => {
        const value = parseFloat(params.value || 0);
        const color = value >= 85 ? colors.success.main : value >= 50 ? colors.warning.main : value > 0 ? colors.error.main : colors.grey[400];
        return params.row.total_target > 0 ? (
          <Chip label={`${value}%`} size="small"
            sx={{ fontWeight: 600, fontSize: '0.75rem', bgcolor: `${color}15`, color }} />
        ) : (
          <Typography variant="caption" color="text.secondary">-</Typography>
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      display: 'flex',
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => setActionMenu({ anchorEl: e.currentTarget, row: params.row })}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title="Financial Periods"
          subtitle="Manage financial periods and revenue tracking"
          breadcrumbs={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'Finance & Budget', path: '/finance/dashboard' },
            { label: 'Financial Periods' },
          ]}
          action={
            can('finance.manage_periods') ? <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
            >
              Create Period
            </Button> : undefined
          }
        />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load financial periods. Please try again.
          </Alert>
        )}

        <Card>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, pb: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search periods..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Box>

            {isLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ height: 600, width: '100%' }}>
                <DataGrid
                  rows={periods?.data || []}
                  columns={columns}
                  pageSizeOptions={[10, 25, 50, 100]}
                  paginationModel={{ page, pageSize: rowsPerPage }}
                  onPaginationModelChange={(model) => {
                    setPage(model.page);
                    setRowsPerPage(model.pageSize);
                  }}
                  rowCount={periods?.meta?.total || periods?.total || 0}
                  paginationMode="server"
                  disableRowSelectionOnClick
                  rowHeight={52}
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-cell': {
                      borderBottom: `1px solid ${colors.grey[200]}`,
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: colors.grey[50],
                      borderBottom: `2px solid ${colors.grey[300]}`,
                      borderTop: `1px solid ${colors.grey[200]}`,
                    },
                    '& .MuiDataGrid-footerContainer': {
                      borderTop: `2px solid ${colors.grey[300]}`,
                    },
                  }}
                />
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Actions Menu */}
        <Menu
          anchorEl={actionMenu.anchorEl}
          open={Boolean(actionMenu.anchorEl)}
          onClose={() => setActionMenu({ anchorEl: null, row: null })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={() => {
            navigate(`/finance/streams/${actionMenu.row?.id}`);
            setActionMenu({ anchorEl: null, row: null });
          }}>
            <ListItemIcon><TrendingUp fontSize="small" /></ListItemIcon>
            <ListItemText>Manage Streams</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => {
            handleOpenDialog(actionMenu.row, false);
            setActionMenu({ anchorEl: null, row: null });
          }}>
            <ListItemIcon><Visibility fontSize="small" /></ListItemIcon>
            <ListItemText>View Details</ListItemText>
          </MenuItem>
          {can('finance.manage_periods') && actionMenu.row?.status === 'active' && (
            <MenuItem onClick={() => {
              handleOpenDialog(actionMenu.row, true);
              setActionMenu({ anchorEl: null, row: null });
            }}>
              <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Edit Period</ListItemText>
            </MenuItem>
          )}
          {can('finance.manage_periods') && actionMenu.row?.status === 'active' && (
            <MenuItem onClick={() => {
              handleClosePeriod(actionMenu.row.id);
              setActionMenu({ anchorEl: null, row: null });
            }}>
              <ListItemIcon><Lock fontSize="small" color="error" /></ListItemIcon>
              <ListItemText sx={{ color: 'error.main' }}>Close Period</ListItemText>
            </MenuItem>
          )}
        </Menu>

        {/* Create/View Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {!selectedPeriod ? 'Create Financial Period' : isEditing ? 'Edit Financial Period' : 'Period Details'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              {selectedPeriod && !isEditing && selectedPeriod.status === 'closed' && (
                <Alert severity="info" sx={{ py: 0.5 }}>This period is closed and cannot be edited.</Alert>
              )}
              <TextField
                fullWidth
                label="Period Name"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                disabled={selectedPeriod && !isEditing}
              />
              {(!selectedPeriod || isEditing) && (
                <FormControl fullWidth required>
                  <InputLabel>Period Type</InputLabel>
                  <Select
                    value={formData.period_type}
                    label="Period Type"
                    onChange={(e) => setFormData({ ...formData, period_type: e.target.value })}
                    disabled={selectedPeriod && !isEditing}
                  >
                    <MenuItem value="financial_year">Financial Year</MenuItem>
                    <MenuItem value="calendar_year">Calendar Year</MenuItem>
                  </Select>
                </FormControl>
              )}
              {selectedPeriod && !isEditing && selectedPeriod.period_type && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Period Type</Typography>
                  <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                    {selectedPeriod.period_type.replace('_', ' ')}
                  </Typography>
                </Box>
              )}
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                required
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                disabled={selectedPeriod && !isEditing}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="End Date"
                type="date"
                required
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                disabled={selectedPeriod && !isEditing}
                InputLabelProps={{ shrink: true }}
              />
              {selectedPeriod && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Box mt={0.5}>
                    <Chip
                      label={selectedPeriod.status}
                      color={getStatusColor(selectedPeriod.status)}
                      size="small"
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Close</Button>
            {!selectedPeriod && (
              <Button
                variant="contained"
                onClick={handleCreatePeriod}
                disabled={saving || !formData.name || !formData.start_date || !formData.end_date}
              >
                {saving ? 'Creating...' : 'Create'}
              </Button>
            )}
            {selectedPeriod && isEditing && (
              <Button
                variant="contained"
                onClick={handleUpdatePeriod}
                disabled={saving || !formData.name || !formData.start_date || !formData.end_date}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Close Period Confirmation Dialog */}
        <Dialog open={closureDialog.open} onClose={() => setClosureDialog({ open: false, periodId: null, summary: null })} maxWidth="sm" fullWidth>
          <DialogTitle>Close Financial Period</DialogTitle>
          <DialogContent>
            {closureDialog.summary && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <Alert severity="warning">
                  Closing this period will prevent any further modifications to its streams and targets.
                </Alert>
                <Typography variant="subtitle2">Period: <strong>{closureDialog.summary.period?.name}</strong></Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Card sx={{ flex: 1, bgcolor: colors.grey[50] }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Typography variant="caption" color="text.secondary">Streams</Typography>
                      <Typography variant="h6" fontWeight={600}>{closureDialog.summary.streams_count}</Typography>
                    </CardContent>
                  </Card>
                  <Card sx={{ flex: 1, bgcolor: colors.grey[50] }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Typography variant="caption" color="text.secondary">Target</Typography>
                      <Typography variant="h6" fontWeight={600}>KES {parseFloat(closureDialog.summary.total_target || 0).toLocaleString()}</Typography>
                    </CardContent>
                  </Card>
                  <Card sx={{ flex: 1, bgcolor: colors.grey[50] }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Typography variant="caption" color="text.secondary">Collected</Typography>
                      <Typography variant="h6" fontWeight={600} color="success.main">KES {parseFloat(closureDialog.summary.total_collected || 0).toLocaleString()}</Typography>
                    </CardContent>
                  </Card>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Achievement: <strong>{closureDialog.summary.progress}%</strong>
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setClosureDialog({ open: false, periodId: null, summary: null })}>Cancel</Button>
            <Button variant="contained" color="error" onClick={confirmClosePeriod} disabled={saving}>
              {saving ? 'Closing...' : 'Close Period'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Setup Prompt after creating period */}
        <Dialog open={setupPrompt.open} maxWidth="sm" fullWidth>
          <DialogTitle>Financial Period Created</DialogTitle>
          <DialogContent>
            <Alert severity="success" sx={{ mb: 3 }}>
              <strong>{setupPrompt.periodName}</strong> has been created successfully.
            </Alert>
            <Typography variant="body1" sx={{ mb: 2 }}>
              To start tracking revenue, you need to:
            </Typography>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                <Chip label="1" size="small" color="primary" sx={{ fontWeight: 700, minWidth: 28 }} />
                <Typography variant="body2"><strong>Create Revenue Streams</strong> — group your services into streams (e.g., Business Licenses, Market Fees)</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Chip label="2" size="small" color="primary" sx={{ fontWeight: 700, minWidth: 28 }} />
                <Typography variant="body2"><strong>Set Ward Targets</strong> — define collection targets per ward for each stream</Typography>
              </Box>
            </Box>
            <Alert severity="info" sx={{ py: 0.5 }}>
              Without streams and targets, this period will show no data on the finance dashboard.
            </Alert>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setSetupPrompt({ open: false, periodId: null, periodName: '' })}>
              I'll do it later
            </Button>
            <Button variant="contained" onClick={() => {
              setSetupPrompt({ open: false, periodId: null, periodName: '' });
              if (setupPrompt.periodId) {
                navigate(`/finance/streams/${setupPrompt.periodId}`);
              }
            }}
              startIcon={<TrendingUp />}>
              Set Up Streams & Targets
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};
